<div (click)="handleTriggerClick($event)">
  @if (customTriggerTemplateRef && customValueTemplateRef) {
    @if (hasValue) {
      <ng-container
        [ngTemplateOutlet]="customValueTemplateRef"
        [ngTemplateOutletContext]="{
          startDate: selectedStartDate(),
          endDate: selectedEndDate(),
          hasError: hasError,
        }"
      />
    } @else {
      <ng-container
        [ngTemplateOutlet]="customTriggerTemplateRef"
        [ngTemplateOutletContext]="{
          hasError: hasError,
        }"
      />
    }
  } @else {
    <ng-container [ngTemplateOutlet]="defaultTriggerAndValueTemplate" />
  }
</div>

<ng-template #defaultTriggerAndValueTemplate>
  <div class="flex min-w-[17rem] items-center gap-1">
    <div
      class="grid min-h-[2.75rem] grow select-none grid-cols-[auto_auto_auto] items-center justify-start gap-x-0.5 whitespace-nowrap rounded border border-slate-300 px-3 py-2 outline-none"
      [ngClass]="
        disabled
          ? 'cursor-not-allowed bg-slate-50 text-slate-400'
          : 'cursor-pointer bg-white'
      "
    >
      @if (!_startDate() || !_endDate()) {
        {{ displayPlaceholder() }}
      } @else {
        @if (isRangeSelection()) {
          <div class="col-span-1 w-full">{{ selectedStartDate() }}</div>
          <div class="col-span-1">{{ separator() }}</div>
          <div class="col-span-1 w-full">{{ selectedEndDate() }}</div>
        }
        @if (isSingleSelection()) {
          <div class="col-span-3 w-full">{{ selectedStartDate() }}</div>
        }
      }
    </div>

    <p-button [disabled]="disabled">
      <ng-template #icon>
        <svg-icon src="assets/icons/general/calendar.svg" />
      </ng-template>
    </p-button>
  </div>
</ng-template>

<p-popover #dateRangePicker>
  <div
    class="grid"
    (click)="$event.stopPropagation()"
    [ngClass]="[
      showPresetSelection()
        ? 'grid-cols-[auto_1fr] gap-x-3 gap-y-1'
        : 'grid-cols-1 gap-1',
    ]"
  >
    <ng-container *ngTemplateOutlet="calendarTemplate" />
    @if (showPresetSelection()) {
      <ng-container *ngTemplateOutlet="presetsTemplate" />
    }
    <ng-container *ngTemplateOutlet="controlsTemplate" />
  </div>
</p-popover>

<ng-template #calendarTemplate>
  <div class="col-span-1">
    <!-- month selection -->
    <div class="mb-3 flex items-center justify-between gap-1">
      <ng-select
        class="min-w-[7.5rem]"
        [items]="months"
        [(ngModel)]="currentMonth"
        [placeholder]="currentMonth"
        [clearable]="false"
        (change)="changeCurrentMonth($event)"
      />

      <ng-select
        class="min-w-[6rem]"
        [items]="years"
        [(ngModel)]="currentYear"
        [clearable]="false"
        [placeholder]="currentYear"
        (change)="changeCurrentYear($event)"
      />

      <div class="flex">
        <p-button
          variant="text"
          severity="contrast"
          (click)="generatePreviousMonthCalendar(); $event.stopPropagation()"
          pTooltip="Previous month"
          tooltipPosition="top"
        >
          <ng-template #icon>
            <svg-icon src="assets/icons/general/chevron-left.svg" />
          </ng-template>
        </p-button>
        <p-button
          variant="text"
          severity="contrast"
          (click)="generateNextMonthCalendar(); $event.stopPropagation()"
          pTooltip="Next month"
          tooltipPosition="top"
        >
          <ng-template #icon>
            <svg-icon src="assets/icons/general/chevron-right.svg" />
          </ng-template>
        </p-button>
      </div>
    </div>

    <div class="grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] gap-x-3">
      @let weeksArray = weeks();
      @let datesArray = dates();
      @let stylesMap = dateStyles();
      <!-- days of week-->
      <div
        class="col-start-2 col-end-3 row-start-1 row-end-2 mb-3 grid grid-cols-7"
      >
        @for (weekDay of weekDays; track weekDay) {
          <div class="p-1 text-center text-xs font-semibold">
            {{ weekDay }}
          </div>
        }
      </div>

      <!-- days of month -->
      <div
        class="col-start-2 col-end-3 row-start-2 row-end-3 grid grid-cols-7"
        [ngClass]="[
          weeksArray.length === 4 ? 'grid-rows-4' : '',
          weeksArray.length === 5 ? 'grid-rows-5' : '',
          weeksArray.length === 6 ? 'grid-rows-6' : '',
        ]"
      >
        <!-- day padding -->
        @for (dp of datesPadding; track $index) {
          <div class="select-none rounded p-1 text-center">
            {{ dp }}
          </div>
        }

        <!-- days -->
        @for (d of datesArray; track d) {
          <button
            [ngClass]="stylesMap.get(d)"
            (click)="selectDate(d); $event.stopPropagation()"
          >
            {{ d | date: "d" }}
          </button>
        }
      </div>

      <!-- weeks of year -->
      @if (showWeekSelection() && isRangeSelection()) {
        <div
          class="col-start-1 col-end-2 row-start-1 row-end-3 grid grid-cols-1"
          [ngClass]="[
            weeksArray.length === 4 ? 'grid-rows-[auto_repeat(4,1fr)]' : '',
            weeksArray.length === 5 ? 'grid-rows-[auto_repeat(5,1fr)]' : '',
            weeksArray.length === 6 ? 'grid-rows-[auto_repeat(6,1fr)]' : '',
          ]"
        >
          <div class="mb-3 p-1 text-center text-xs font-semibold">Week</div>
          @for (week of weeksArray; track week) {
            <ag1-button-small
              [label]="week.week"
              classOverrides="aspect-square"
              variant="tertiary"
              (clickEvent)="selectWeek(week)"
            />
          }
        </div>
      }
    </div>
  </div>
</ng-template>

<ng-template #presetsTemplate>
  <div
    class="col-span-1 row-span-2 flex max-h-[22rem] flex-col justify-center gap-3 overflow-y-auto border-l pl-3"
  >
    @for (presetOption of presetOptions; track presetOption) {
      <ag1-button-small
        [variant]="
          presetOption === selectedPresetOption ? 'secondary' : 'tertiary'
        "
        classOverrides="w-full !justify-start capitalize"
        (clickEvent)="selectPreset(presetOption)"
        [label]="getPresetDisplayName(presetOption)"
      />
    }
  </div>
</ng-template>

<ng-template #controlsTemplate>
  <div class="mt-3 flex justify-end gap-2">
    @if (clearable()) {
      <p-button
        (click)="clearDates()"
        variant="text"
        severity="contrast"
        label="Clear"
      />
    }
    <p-button (click)="applyDateRange()" label="Apply" />
  </div>
</ng-template>
